import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { format, isSameDay } from "date-fns";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { Keyboard } from "@capacitor/keyboard";
import {
  IonContent,
  IonGrid,
  IonPage,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonItem,
  IonAvatar,
  IonList,
  IonTextarea,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonLoading,
  IonFooter,
  useIonViewWillLeave,
} from "@ionic/react";
import { arrowBack, paperPlaneOutline } from "ionicons/icons";
import { ProfileContext } from "../context/ProfileContext";
import "../theme/AppStyles.css";
import { WerkerJobContext } from "../context/JobContextWerker";
import { SchedJobContext } from "../context/JobContextScheduler";
import { Capacitor } from "@capacitor/core";
import { parseNotes } from "../functions/FX_ParseAndMap";
import { sendChatMessage } from "../utils/chatUtils";
import { useMessageRead } from "../hooks/useMessageRead";
import { useChatMessages } from "../hooks/useChatMessages";
import { useChatType } from "../hooks/useChatType";
import { ChatData, ThisChatData } from "../types/chat";

interface ShiftDetailsProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const Chat: React.FC<ShiftDetailsProps> = ({ match }) => {
  const { profile, myChats, myAssociates } = useContext(ProfileContext);
  const [sendMessage, setSendMessage] = React.useState("");
  const listRef = useRef<HTMLIonListElement>(null);
  const textareaRef = useRef<HTMLIonTextareaElement | null>(null);
  const history = useHistory<any>();
  const { werkerJob } = useContext(WerkerJobContext);
  const { schedJob } = useContext(SchedJobContext);
  const [headerColor, setHeaderColor] = useState("warning");
  const [isSending, setIsSending] = useState(false);
  const { markChatAsRead } = useMessageRead();
  const lastMarkedChatRef = useRef<string>("");

  const blurFocusedElement = useCallback(() => {
    let activeElement: Element | null = document.activeElement;

    while (activeElement && (activeElement as HTMLElement).shadowRoot?.activeElement) {
      activeElement = (activeElement as HTMLElement).shadowRoot?.activeElement || null;
    }

    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      activeElement.blur();
    }

    const hostElement = document.activeElement;
    if (hostElement instanceof HTMLElement && hostElement !== document.body) {
      hostElement.blur();
    }
  }, []);

  useIonViewWillLeave(() => {
    blurFocusedElement();
  });

  // Use shared chat messages hook
  const {
    chatType,
    setPendingMessages,
    renderMessages,
  } = useChatMessages(match.params.id);

  const [thisChat, setThisChat] = useState<ThisChatData>({
    DMuid: "",
    IsRead: true,
    MessageDate: "",
    MessagePreview: "",
    MessageTitle: "",
    SendersPic: "../assets/Briefcase.png",
    Key: "",
  });

  // Use shared chat type hook
  const resolvedChatType = useChatType(chatType, thisChat);


  // Post Message ////////////////////////////////
  const makeClientId = () => `${profile.UserId}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const postMessage = async () => {
    const text = (sendMessage || "").trim();
    if (!text) return;

    const clientId = makeClientId();
    const now = Date.now();
    // ✅ optimistic bubble
    const optimistic: ChatData = {
      id: clientId,
      clientId,
      pending: true,
      failed: false,

      IsRead: true,
      MessageAuthorId: profile.UserId,
      MessageAuthor: `${profile.FirstName} ${profile.LastName}`,
      MessageText: text,
      JobDocId: match.params.id,
      UserActionTakenId: 0,
      UserActionTakenUserId: 0,
      createdAt: now,
      updatedAt: now,
      MessageAuthorPic: profile.ProfilePicURL,
      NewChatPath: "",
    };
    setPendingMessages((prev) => [...prev, optimistic]);
    setSendMessage(""); // ✅ clear immediately
    setIsSending(true);
    try {
      await sendChatMessage(
        match.params.id,
        text,
        resolvedChatType,
        profile,
        myChats,
        myAssociates,
        schedJob,
        werkerJob,
        clientId
      );
    } catch (error) {
      console.error("Error posting message:", error);
      // optional: mark failed
      setPendingMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m))
      );
    } finally {
      setIsSending(false);
    }
  };

  // Mark messages as read ////////////////////////////////
  const updateMsgIsRead = async () => {
    const chatExists = myChats.find((chat: { Key: string }) => chat.Key === match.params.id);
    if (chatExists) {
      await markChatAsRead(match.params.id, resolvedChatType);
    }
  };

  // No chats placeholder ////////////////////////////////
  const NoChats: React.FC = () => {
    return (
      <React.Fragment>
        <IonRow>
          <IonCol
            size="1"
          />
          <IonCol>
            <h4>Start chatting ...</h4>
          </IonCol>
          <IonCol
            size="1"
          />
        </IonRow>
      </React.Fragment>
    );
  };

  // Scroll to bottom function ////////////////////////////////
  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      const lastItem = listRef.current.lastElementChild;
      if (lastItem) {
        lastItem.scrollIntoView({ behavior: "auto", block: "end" });
      }
    }
  }, []);

  // keyboard effect for mobile ////////////////////////////////
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;
    let sub: any;
    const setup = async () => {
      sub = await Keyboard.addListener("keyboardDidShow", () => {
        setTimeout(() => {
          // ✅ hard-coded smooth ONLY for keyboard
          if (listRef.current) {
            const lastItem = listRef.current.lastElementChild;
            lastItem?.scrollIntoView({ behavior: "smooth", block: "end" });
          }
          updateMsgIsRead();
        }, 250);
      });
    };
    setup();
    return () => {
      try {
        sub?.remove?.();
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-scroll effect ////////////////////////////////////
  const lastRenderMessageId = useMemo(() => {
    return renderMessages.length > 0 ? renderMessages[renderMessages.length - 1].id : "";
  }, [renderMessages]);

  // auto-scroll + mark-read (only once per chat open)
  useEffect(() => {
    // scroll whenever last message changes (not just length)
    scrollToBottom();
    // mark read once per chat open, and only once chat type is resolved
    const key = `${match.params.id}_${resolvedChatType}`;
    if (resolvedChatType && lastMarkedChatRef.current !== key) {
      lastMarkedChatRef.current = key;
      updateMsgIsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRenderMessageId, match.params.id, resolvedChatType]);

  // Get this chat details ////////////////////////////////
  useEffect(() => {
    const MyChatsFilter = async () => {
      const thisMyChat = await myChats.find((myChats: any) => myChats.Key === match.params.id);
      if (thisMyChat) {
        setThisChat(thisMyChat);
      } else {
      }
    };
    MyChatsFilter();
  }, [match, myChats]);

  // Set header color based on role ////////////////////////////////
  useEffect(() => {
    if (profile.UserLastRoleScheduler === true) {
      // Scheduler
      setHeaderColor("secondwarning");
    } else {
      // Werker
      setHeaderColor("warning");
    }
  }, [profile]);

  /////////////////////////////////////////////////////////////////////
  // Chat.tsx — UI-only fix: move composer into IonFooter so it doesn't overlay on back nav.
  // Drop-in replacement for your return() markup (keep your existing logic/effects as-is).

  return (
    <IonPage>
      {profile.UserLastRoleScheduler === true && (
        <IonLoading message="WerkApp" cssClass="custom-loading" showBackdrop={true} isOpen={true} duration={300} />
      )}
      {profile.UserLastRoleScheduler !== true && (
        <IonLoading message="WerkApp" cssClass="custom-loading2" showBackdrop={true} isOpen={true} duration={300} />
      )}

      <IonHeader>
        <IonToolbar
          color={headerColor}
        >
          {/* START — Back */}
          <IonButtons
            slot="start"
          >
            <IonButton
              onClick={() => {
                blurFocusedElement();
                history.goBack();
              }}
              fill="clear"
              className="waBackBtn"
            >
              <IonIcon
                size="large"
                className="backArrowColor"
                icon={arrowBack}
              />
            </IonButton>
          </IonButtons>

          {/* CENTER TITLE */}
          <div
            slot="primary"
            style={{
              display: "flex",
              minWidth: 0,
              paddingLeft: 10,
              paddingRight: "10px",
            }}
          >
            <div
              className="waHeaderTitle"
              style={{
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {thisChat.MessageTitle}
            </div>
          </div>

          {/* END — Avatar */}
          <IonButtons
            slot="end"
          >
            <IonButton
              fill="clear"
              className="waAvatarBtn"
            >
              {chatType === "DM" ? (
                <IonAvatar
                  className="tinyavatar2 waAvatar"
                >
                  <img src={thisChat.SendersPic} alt="" />
                </IonAvatar>
              ) : chatType === "JobMsg" && headerColor === "secondwarning" ? (
                <IonAvatar
                  className="tinyavatar2 waAvatar"
                >
                  <img src={"../assets/WerkAppYellow.png"} alt="" />
                </IonAvatar>
              ) : (
                <IonAvatar
                  className="tinyavatar2 waAvatar"
                >
                  <img src={"../assets/WerkAppPro.png"} alt="" />
                </IonAvatar>
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        fullscreen={false}
        scrollX={false}
        scrollY={true}
      >
        <IonGrid>
          {renderMessages.length === 0 && <NoChats />}

          {renderMessages.length > 0 && (
            <IonList
              color="medium"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "0px",
              }}
              className="searchBar"
              ref={listRef}
            >
              {renderMessages.map((message, index, arr) => {
                const prevMessage = arr[index - 1];
                const isNewDay =
                  !prevMessage || !isSameDay(new Date(message.createdAt), new Date(prevMessage.createdAt));

                return (
                  <React.Fragment key={message.id}>
                    {isNewDay && (
                      <div
                        style={{
                          textAlign: "center",
                          margin: "10px 0",
                          marginTop: "25px",
                          color: "#92949c",
                          fontWeight: "bold",
                        }}
                      >
                        {format(new Date(message.createdAt), "MMMM d, yyyy")}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: message.MessageAuthorId === profile.UserId ? "row-reverse" : "row",
                        alignItems: "flex-end",
                        gap: "10px",
                        margin: "12px",
                      }}
                    >
                      {message.MessageAuthorId !== profile.UserId && (
                        <IonAvatar
                          style={{
                            marginLeft: "-12px",
                            width: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        >
                          <img src={message.MessageAuthorPic} alt="" />
                        </IonAvatar>
                      )}

                      {profile.UserLastRoleScheduler !== true && (
                        <div
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            position: "relative",
                            alignSelf: message.MessageAuthorId === profile.UserId ? "flex-end" : "flex-start",
                            backgroundColor: message.MessageAuthorId === profile.UserId ? "#ffcc4d" : "#e8e8e8",
                            color: message.MessageAuthorId === profile.UserId ? "#000000" : "#1e1e1e",
                            maxWidth: message.MessageAuthorId === profile.UserId ? "80%" : "75%",
                            padding: "10px 15px",
                            border: "1px solid",
                            borderRadius: "10px",
                            borderBottomRightRadius: message.MessageAuthorId === profile.UserId ? "0px" : "10px",
                            borderBottomLeftRadius: message.MessageAuthorId === profile.UserId ? "10px" : "0px",
                            minWidth: "55px",
                          }}
                        >
                          <span style={{ fontSize: "12px", color: "#6d6e73" }}>
                            {message.MessageAuthorId !== profile.UserId && <span>{message.MessageAuthor}</span>}
                          </span>
                          <p
                            style={{
                              margin: 0,
                              userSelect: "text",
                              color: message.MessageAuthorId === profile.UserId ? "black" : "",
                            }}
                          >
                            {parseNotes(message.MessageText)}
                          </p>
                          <span
                            style={{
                              fontSize: "12px",
                              minWidth: "55px",
                              color: "#92949c",
                              position: "absolute",
                              bottom: "-18px",
                              right: "0",
                            }}
                          >
                            {message.pending ? "Sending..." : format(new Date(message.createdAt), "h:mm a")}
                          </span>
                        </div>
                      )}

                      {profile.UserLastRoleScheduler === true && (
                        <div
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            position: "relative",
                            alignSelf: message.MessageAuthorId === profile.UserId ? "flex-end" : "flex-start",
                            backgroundColor: message.MessageAuthorId === profile.UserId ? "#E0742E" : "#e8e8e8",
                            color: message.MessageAuthorId === profile.UserId ? "#000000" : "#1e1e1e",
                            maxWidth: message.MessageAuthorId === profile.UserId ? "80%" : "75%",
                            padding: "10px 15px",
                            border: "1px solid",
                            borderRadius: "10px",
                            borderBottomRightRadius: message.MessageAuthorId === profile.UserId ? "0px" : "10px",
                            borderBottomLeftRadius: message.MessageAuthorId === profile.UserId ? "10px" : "0px",
                            userSelect: "text",
                            minWidth: "55px",
                          }}
                        >
                          <span style={{ fontSize: "12px", color: "#92949c", userSelect: "text" }}>
                            {message.MessageAuthorId !== profile.UserId && <span>{message.MessageAuthor}</span>}
                          </span>
                          <p
                            style={{
                              margin: 0,
                              userSelect: "text",
                              color: message.MessageAuthorId === profile.UserId ? "black" : "",
                            }}
                          >
                            {parseNotes(message.MessageText)}
                          </p>
                          <span
                            style={{
                              fontSize: "12px",
                              minWidth: "55px",
                              color: "#92949c",
                              position: "absolute",
                              bottom: "-18px",
                              right: "0",
                              userSelect: "text",
                            }}
                          >
                            {message.pending ? "Sending..." : format(new Date(message.createdAt), "h:mm a")}
                          </span>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              <br />
            </IonList>
          )}
        </IonGrid>
      </IonContent>

      {/* ✅ Composer moved into IonFooter to prevent overlay/jank on back navigation */}
      <IonFooter
      >
        <IonToolbar
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <IonGrid
            style={{ padding: 0 }}
          >
            <IonRow>
              <IonCol
                size="10"
              >
                <IonItem
                  counter={true}
                  lines="none"
                >
                  <IonTextarea
                    ref={textareaRef}
                    spellCheck="true"
                    autoCapitalize="sentences"
                    autoGrow={true}
                    rows={1}
                    maxlength={1000}
                    enterkeyhint="send"
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void postMessage();
                      }
                    }}
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      overflow: "auto",
                    }}
                    value={sendMessage}
                    placeholder="Send Message..."
                    onIonChange={(e) => setSendMessage(e.detail.value!)}
                  />
                </IonItem>
              </IonCol>

              <IonCol
                size="2"
              >
                <IonButton
                  type="button"
                  fill="clear"
                  disabled={isSending}
                  onPointerDown={(e) => {
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onClick={() => void postMessage()}
                >
                  <IonIcon
                    size="small"
                    icon={paperPlaneOutline}
                  />
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Chat;
